/**
 * The PAM player card and support surface (ADR-047, task B3).
 *
 * Kept in its own module rather than folded into `client.ts` so it owns its
 * types outright — the wire shapes here mirror `server/src/admin/wire.ts` field
 * for field. Every function is LIVE; in `MOCK_MODE` the reads degrade to empty
 * structures so `pnpm dev` renders the empty states instead of throwing, and
 * the writes fail honestly against the real API (the same rule the key manager
 * keeps: a form that appears to store and does not is worse than one that
 * errors).
 */
import { http } from './http.ts';
import { MOCK_MODE } from './client.ts';
import type { Currency, LedgerKind, PlayerSummary, UserStatus } from './types.ts';

// ------------------------------------------------------------------- types

export interface PlayerProfile {
  countryCode: string | null;
  locale: string | null;
  birthdate: string | null;
  acquisitionSource: string | null;
  marketingOptIn: boolean | null;
  lastLoginAt: string | null;
  retentionScore: string | null;
  retentionScoredAt: string | null;
}

export interface CurrencyBalance {
  currency: Currency;
  balance: string;
  deposited: string;
  withdrawn: string;
  wagered: string;
  won: string;
}

export interface PlayerSession {
  id: number;
  createdAt: string;
  expiresAt: string;
  revokedAt: string | null;
  active: boolean;
}

export interface RiskFlagView {
  id: number;
  userId: number;
  username: string | null;
  kind: string;
  detail: unknown;
  status: string;
  createdAt: string;
  clearedAt: string | null;
}

export interface BonusGrantView {
  id: number;
  currency: Currency;
  amount: string;
  lockedRemaining: string;
  wagerRequired: string;
  wagerProgress: string;
  status: string;
  expiresAt: string;
  createdAt: string;
}

export interface PlayerNoteView {
  id: number;
  note: string;
  pinned: boolean;
  operatorId: number;
  operatorUsername: string | null;
  createdAt: string;
}

export type TicketStatus = 'open' | 'pending' | 'resolved';
export type TicketPriority = 'low' | 'normal' | 'high';
export type TicketAuthorType = 'player' | 'operator' | 'system';

/**
 * The nullable AI triage seam (ADR-056, filled by A-AI-6): classification plus
 * a DRAFT reply a human may use. `disposition: 'draft'` marks a usable
 * proposal ('unparsed' records a model reply that could not be parsed); the
 * draft is NEVER sent automatically — a human sends every reply.
 */
export interface TicketTriage {
  category?: string;
  priority?: string;
  draft?: string;
  disposition?: string;
  note?: string;
  model?: string;
}

export interface TicketSummary {
  id: number;
  userId: number;
  username: string | null;
  subject: string;
  status: TicketStatus;
  priority: TicketPriority;
  assignedOperatorId: number | null;
  assignedOperatorUsername: string | null;
  triage: TicketTriage | null;
  messageCount: number;
  lastMessageAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface TicketMessageView {
  id: number;
  ticketId: number;
  authorType: TicketAuthorType;
  operatorId: number | null;
  operatorUsername: string | null;
  body: string;
  createdAt: string;
}

export interface TicketDetail extends TicketSummary {
  messages: TicketMessageView[];
}

/**
 * One row of the player's own audit history (ADR-068).
 *
 * The operator reads EXACTLY the rows the player reads at `GET /me/events` —
 * nothing hidden, nothing added. That symmetry is what makes the log usable in
 * a dispute: both sides quote one record.
 *
 * `detail` is ids, kinds, counts and masked fingerprints by construction on the
 * server. It is rendered defensively anyway — see `EventsTab`.
 */
export interface PlayerEventView {
  id: number;
  type: string;
  detail: unknown;
  ip: string | null;
  userAgent: string | null;
  /** `player`, `operator` or `system`. */
  actor: string;
  createdAt: string;
}

export interface PlayerCard {
  player: PlayerSummary;
  profile: PlayerProfile;
  balances: CurrencyBalance[];
  sessions: PlayerSession[];
  riskFlags: RiskFlagView[];
  bonusGrants: BonusGrantView[];
  notes: PlayerNoteView[];
  tickets: TicketSummary[];
  /** The most recent page of the player's history; the tab pages back further. */
  events: PlayerEventView[];
}

export interface LedgerEntry {
  id: number;
  amount: string;
  kind: LedgerKind;
  refType: string;
  refId: number;
  currency: Currency;
  balanceAfter: string;
  createdAt: string;
}

// --------------------------------------------------------------- the card

/**
 * LIVE — `GET /internal/admin/players/:id`. The whole card in one call.
 *
 * In mock mode the extras are empty and the balances collapse to the summary's
 * single-currency figures, so the card renders from what the list already
 * holds instead of throwing.
 */
export async function fetchPlayerCard(userId: number, fallback: PlayerSummary): Promise<PlayerCard> {
  if (MOCK_MODE) {
    return {
      player: fallback,
      profile: {
        countryCode: null,
        locale: null,
        birthdate: null,
        acquisitionSource: null,
        marketingOptIn: null,
        lastLoginAt: fallback.lastActiveAt,
        retentionScore: null,
        retentionScoredAt: null,
      },
      balances: [
        {
          currency: fallback.currency,
          balance: fallback.balance,
          deposited: fallback.depositedTotal,
          withdrawn: fallback.withdrawnTotal,
          wagered: fallback.turnover,
          won: '0.0000',
        },
      ],
      sessions: [],
      riskFlags: [],
      bonusGrants: [],
      notes: [],
      tickets: [],
      events: [],
    };
  }
  return http.get<PlayerCard>(`/internal/admin/players/${userId}`);
}

// ------------------------------------------------- player events (ADR-068)

export interface PlayerEventPage {
  events: PlayerEventView[];
  /** Lifetime count by type, over the whole history rather than this page. */
  counts: Record<string, number>;
}

/**
 * LIVE — `GET /internal/admin/players/:id/events`. Viewer role.
 *
 * Newest first, keyset-paginated on the id: `before` is the id to read
 * backwards from, never an offset, so a row written mid-scroll cannot shift the
 * page under the operator.
 */
export async function fetchPlayerEvents(
  userId: number,
  limit = 50,
  before?: number,
): Promise<PlayerEventPage> {
  if (MOCK_MODE) return { events: [], counts: {} };
  return http.get<PlayerEventPage>(`/internal/admin/players/${userId}/events`, {
    limit,
    ...(before === undefined ? {} : { before }),
  });
}

/** LIVE — `GET /internal/admin/players/:id/statement`. */
export async function fetchStatement(userId: number, limit = 50): Promise<LedgerEntry[]> {
  if (MOCK_MODE) return [];
  const body = await http.get<{ entries: LedgerEntry[] }>(
    `/internal/admin/players/${userId}/statement`,
    { limit },
  );
  return body.entries;
}

/** LIVE — `PATCH /internal/admin/players/:id` (status change). Returns the card. */
export async function setPlayerStatus(
  userId: number,
  status: UserStatus,
  reason: string,
): Promise<PlayerCard> {
  return http.patch<PlayerCard>(`/internal/admin/players/${userId}`, { status, reason });
}

export interface ProfilePatch {
  countryCode?: string | null;
  locale?: string | null;
  marketingOptIn?: boolean | null;
}

/** LIVE — `PATCH /internal/admin/players/:id` (profile edit). Returns the card. */
export async function updatePlayerProfile(userId: number, patch: ProfilePatch): Promise<PlayerCard> {
  return http.patch<PlayerCard>(`/internal/admin/players/${userId}`, patch);
}

// -------------------------------------------------------------------- notes

/** LIVE — `GET /internal/admin/players/:id/notes`. */
export async function fetchNotes(userId: number): Promise<PlayerNoteView[]> {
  if (MOCK_MODE) return [];
  const body = await http.get<{ notes: PlayerNoteView[] }>(`/internal/admin/players/${userId}/notes`);
  return body.notes;
}

/** LIVE — `POST /internal/admin/players/:id/notes`. Append-only; approver role. */
export async function addNote(userId: number, note: string): Promise<PlayerNoteView> {
  return http.post<PlayerNoteView>(`/internal/admin/players/${userId}/notes`, { note });
}

// ------------------------------------------------------------------ tickets

export interface TicketQuery {
  status?: TicketStatus;
  /** An operator id, `me` for the caller, or `unassigned`. */
  assigned?: number | 'me' | 'unassigned';
}

/** LIVE — `GET /internal/admin/tickets?status=&assigned=`. */
export async function fetchTickets(query: TicketQuery = {}): Promise<TicketSummary[]> {
  if (MOCK_MODE) return [];
  const body = await http.get<{ tickets: TicketSummary[] }>('/internal/admin/tickets', {
    status: query.status,
    assigned: query.assigned,
  });
  return body.tickets;
}

/** LIVE — `GET /internal/admin/tickets/:id`. */
export async function fetchTicket(id: number): Promise<TicketDetail> {
  return http.get<TicketDetail>(`/internal/admin/tickets/${id}`);
}

/** LIVE — `POST /internal/admin/tickets/:id/reply`. Approver role, audited. */
export async function replyTicket(id: number, body: string): Promise<TicketDetail> {
  return http.post<TicketDetail>(`/internal/admin/tickets/${id}/reply`, { body });
}

export interface TicketPatch {
  status?: TicketStatus;
  priority?: TicketPriority;
  assignedOperatorId?: number | 'me' | null;
}

/** LIVE — `PATCH /internal/admin/tickets/:id`. Assign / status / priority. Audited. */
export async function patchTicket(id: number, patch: TicketPatch): Promise<TicketDetail> {
  return http.patch<TicketDetail>(`/internal/admin/tickets/${id}`, patch);
}

// --------------------------------------------------------------- risk flags

/** LIVE — `GET /internal/admin/risk/flags?status=open`. The AI monitor's output. */
export async function fetchRiskFlags(status = 'open'): Promise<RiskFlagView[]> {
  if (MOCK_MODE) return [];
  const body = await http.get<{ flags: RiskFlagView[] }>('/internal/admin/risk/flags', { status });
  return body.flags;
}

// ---------------------------------------------------- VIP override (ADR-066)

export interface AdminVipView {
  userId: number;
  xp: string;
  derivedTier: number;
  tierOverride: number | null;
  effectiveTier: number;
  effectiveName: string;
  rakebackBps: number;
  unclaimedRakeback: { currency: Currency; amount: string }[];
}

/** LIVE — `GET /internal/admin/players/:id/vip`. The card's VIP standing. */
export async function fetchPlayerVip(userId: number): Promise<AdminVipView> {
  if (MOCK_MODE) {
    return {
      userId,
      xp: '0.0000',
      derivedTier: 0,
      tierOverride: null,
      effectiveTier: 0,
      effectiveName: 'Bronze',
      rakebackBps: 0,
      unclaimedRakeback: [],
    };
  }
  return http.get<AdminVipView>(`/internal/admin/players/${userId}/vip`);
}

/**
 * LIVE — `PUT /internal/admin/players/:id/vip-override`. Admin only, audited
 * (`vip.override_set` / `vip.override_clear`). `tier: null` clears the pin.
 */
export async function setVipOverride(
  userId: number,
  tier: number | null,
  reason: string,
): Promise<AdminVipView> {
  return http.put<AdminVipView>(`/internal/admin/players/${userId}/vip-override`, { tier, reason });
}
